import { Component, Input, OnInit } from '@angular/core';
import { Chargepoint, ChargepointStatus, OCPPChargepoint } from '../../../api/chargepoints-api.service';
import { ChargepointConfiguration, ChargepointsOcppApiService } from '../../../api/chargepoints-ocpp-api.service';
import { Observable, switchMap, filter, map, tap, catchError, of, take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { RefreshService } from '../../common/refresh-widget/refresh.service';
import { findExpectedConfiguration } from '../expected-configuration.data';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { MatDividerModule } from '@angular/material/divider';
interface EditableConfiguration extends ChargepointConfiguration {
  editableValue: string;
  expectedConfigurationError: boolean;
  expectedConfiguration?: string;
}

@Component({
  selector: 'app-chargepoint-configuration-ocpp',
  templateUrl: './chargepoint-configuration-ocpp.component.html',
  styleUrls: ['./chargepoint-configuration-ocpp.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatCardModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatDividerModule
  ]
})
export class ChargepointConfigurationOcppComponent implements OnInit {

  @Input() chargepoint!: OCPPChargepoint;

  @Input() refreshTrigger?: Observable<void>;

  public configuration$!: Observable<EditableConfiguration[]>;

  public expectedConfigurationErrors$!: Observable<string[]>;
  public expectedConfigurationErrorsFixStatus: { [key: string]: 'todo' | 'pending' | 'success' | 'error' } = {};

  public rebootRequired = false;

  public loading = false;
  public fetchingError = false;

  public alreadyFetchedAtLeastOnce = false;

  constructor(private chargepointsOcppApiService: ChargepointsOcppApiService, private snackBar: MatSnackBar, private refreshService: RefreshService, private dialog: MatDialog) {}

  ngOnInit(): void {
    const trigger: Observable<void> = this.refreshTrigger ?? this.refreshService.refreshTrigger;
    this.configuration$ = trigger.pipe(
      filter(() => !!this.chargepoint && this.chargepoint?.status !== ChargepointStatus.UNREACHABLE),
      tap(() => {
        this.loading = true;
        this.fetchingError = false;
        this.alreadyFetchedAtLeastOnce = true;
      }),
      switchMap(() => this.chargepointsOcppApiService.getChargepointConfiguration(this.chargepoint.id)),
      map(configuration => configuration.map(config => ({ ...config, editableValue: config.value }))),
      tap(() => {
        this.loading = false;
        this.fetchingError = false;
      }),
      catchError(() => {
        this.loading = false;
        this.fetchingError = true;
        return of(null);
      }),
      filter(configuration => configuration !== null),
      map(configuration => configuration.map(config => {
        const expectedConfiguration = findExpectedConfiguration(config.key, { vendor: this.chargepoint.chargePointVendor, model: this.chargepoint.chargePointModel, firmwareVersion: this.chargepoint.firmwareVersion });

        return {
          ...config,
          expectedConfigurationError: expectedConfiguration?.expectedValue ? expectedConfiguration.expectedValue !== config.value : false,
          expectedConfiguration: expectedConfiguration?.expectedValue
        };
      })),
      map(configuration => configuration.sort((c1, c2) => c1.expectedConfigurationError ? -1 : c2.expectedConfigurationError ? 1 : 0))
    );

    this.expectedConfigurationErrors$ = this.configuration$.pipe(
      tap(() => {
        this.rebootRequired = false;
        this.expectedConfigurationErrorsFixStatus = {};
      }),
      map(configuration => configuration.filter(config => config.expectedConfigurationError).map(config => config.key))
    );
  }

  public updateConfiguration(config: EditableConfiguration) {
    this.invokeUpdateConfiguration(config.key, config.editableValue);
  }

  public applyExpectedConfiguration(config: EditableConfiguration) {
    this.invokeUpdateConfiguration(config.key, config.expectedConfiguration);
  }

  private invokeUpdateConfiguration(key: string, value?: string) {
    if (this.chargepoint && key && value) {
      this.chargepointsOcppApiService.updateChargepointConfiguration(this.chargepoint.id, {
        key,
        value
      }).subscribe((changeConfigurationStatus: string) => {
        switch ((changeConfigurationStatus || '').toLowerCase()) {
          case 'accepted':
            this.snackBar.open('Configuration updated', 'Close', { duration: 3000 });
            break;
          case 'rejected':
            this.snackBar.open('Configuration update rejected', 'Close', { duration: 5000 });
            break;
          case 'rebootrequired':
            this.snackBar.open('Reboot required', 'Close', { duration: 5000 });
            break;
          case 'notsupported':
          default:
            this.snackBar.open('Configuration update failed', 'Close', { duration: 3000 });
        }
      });
    }
  }

  public applyAllExpectedConfiguration() {
    this.configuration$.pipe(
      map(configuration => configuration.filter(config => config.expectedConfigurationError)),
      tap(configuration => configuration.forEach(config => this.expectedConfigurationErrorsFixStatus[config.key] = 'todo')),
      take(1)
    ).subscribe(async (configuration) => {
      let rebootRequired = false;
      for (const config of configuration) {
        this.expectedConfigurationErrorsFixStatus[config.key] = 'pending';
        if (this.chargepoint && config.expectedConfiguration && config.key) {
          await this.chargepointsOcppApiService.updateChargepointConfiguration(this.chargepoint.id, {
            key: config.key,
            value: config.expectedConfiguration
          }).pipe(
            tap((changeConfigurationStatus: string) => {
              switch ((changeConfigurationStatus || '').toLowerCase()) {
                case 'accepted':
                  this.expectedConfigurationErrorsFixStatus[config.key] = 'success';
                  break;
                case 'rebootrequired':
                  rebootRequired = true;
                  this.expectedConfigurationErrorsFixStatus[config.key] = 'success';
                  break;
                case 'rejected':
                case 'notsupported':
                default:
                  this.expectedConfigurationErrorsFixStatus[config.key] = 'error';
              }
          }),
          ).toPromise();
        }
      }
      if (rebootRequired) {
        this.rebootRequired = true;
      }
    });
  }

  public reboot() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Reboot chargepoint',
        content: 'Are you sure you want to reboot the chargepoint?',
        confirmLabel: 'Reboot',
        cancelLabel: 'Cancel'
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.chargepointsOcppApiService.reboot(this.chargepoint.id, true).subscribe(() => {
          this.snackBar.open('Rebooting chargepoint', 'Close', { duration: 5000 });
          setTimeout(() => {
            this.refreshService.trigger();
          }, 2500);
        });
      }
    });
  }
}